import { useMemo, useState, type ReactNode } from 'react';
import {
  ArrowRight,
  Boxes,
  CheckCircle2,
  Copy,
  LayoutGrid,
  Plus,
  Scissors,
  Store,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

type Format = {
  id: string;
  name: string;
  quantity: number;
};

type CampaignGroup = {
  id: string;
  name: string;
  stores: string[];
  formats: Format[];
  task: string;
};

const initialGroups: CampaignGroup[] = [
  {
    id: 'standard-pos',
    name: 'Standard POS',
    stores: ['Dublin 01', 'Cork Wilton', 'Galway Retail Park', 'Limerick Crescent', 'Waterford'],
    formats: [
      { id: 'a3', name: 'A3 Poster', quantity: 25 },
      { id: 'window', name: 'Window Cling', quantity: 25 },
      { id: 'counter', name: 'Counter Card', quantity: 50 },
    ],
    task: 'Install POS',
  },
];

export default function RestructurePrototype() {
  const [groups, setGroups] = useState<CampaignGroup[]>(initialGroups);
  const [selectedGroupId, setSelectedGroupId] = useState(initialGroups[0].id);
  const [splitMode, setSplitMode] = useState<'formats' | 'stores' | 'matrix'>('formats');
  const [selectedFormats, setSelectedFormats] = useState<Set<string>>(new Set(['window', 'counter']));
  const [selectedStores, setSelectedStores] = useState<Set<string>>(new Set(['Galway Retail Park', 'Waterford']));
  const [newGroupName, setNewGroupName] = useState('Window + Counter POS');

  const selectedGroup = groups.find((group) => group.id === selectedGroupId) ?? groups[0];

  const campaignSummary = useMemo(() => {
    const uniqueStores = new Set(groups.flatMap((group) => group.stores));
    const uniqueFormats = new Set(groups.flatMap((group) => group.formats.map((format) => format.id)));

    return {
      groups: groups.length,
      stores: uniqueStores.size,
      formats: uniqueFormats.size,
      quantity: groups.reduce(
        (total, group) => total + group.formats.reduce((sum, format) => sum + format.quantity, 0),
        0,
      ),
    };
  }, [groups]);

  const toggleFormat = (formatId: string) => {
    setSelectedFormats((current) => {
      const next = new Set(current);
      next.has(formatId) ? next.delete(formatId) : next.add(formatId);
      return next;
    });
  };

  const toggleStore = (storeName: string) => {
    setSelectedStores((current) => {
      const next = new Set(current);
      next.has(storeName) ? next.delete(storeName) : next.add(storeName);
      return next;
    });
  };

  const splitSelectedFormats = () => {
    if (!selectedGroup || selectedFormats.size === 0) return;
    const movingFormats = selectedGroup.formats.filter((format) => selectedFormats.has(format.id));
    if (movingFormats.length === 0 || movingFormats.length === selectedGroup.formats.length) return;

    const sourceGroup: CampaignGroup = {
      ...selectedGroup,
      formats: selectedGroup.formats.filter((format) => !selectedFormats.has(format.id)),
    };
    const newGroup: CampaignGroup = {
      ...selectedGroup,
      id: `group-${Date.now()}`,
      name: newGroupName.trim() || `${selectedGroup.name} split`,
      formats: movingFormats,
    };

    setGroups((current) => current.map((group) => (group.id === sourceGroup.id ? sourceGroup : group)).concat(newGroup));
    setSelectedGroupId(newGroup.id);
    setSelectedFormats(new Set());
  };

  const splitSelectedStores = () => {
    if (!selectedGroup || selectedStores.size === 0) return;
    const movingStores = selectedGroup.stores.filter((storeName) => selectedStores.has(storeName));
    if (movingStores.length === 0 || movingStores.length === selectedGroup.stores.length) return;

    const sourceGroup: CampaignGroup = {
      ...selectedGroup,
      stores: selectedGroup.stores.filter((storeName) => !selectedStores.has(storeName)),
    };
    const newGroup: CampaignGroup = {
      ...selectedGroup,
      id: `group-${Date.now()}`,
      name: newGroupName.trim() || `${selectedGroup.name} split`,
      stores: movingStores,
    };

    setGroups((current) => current.map((group) => (group.id === sourceGroup.id ? sourceGroup : group)).concat(newGroup));
    setSelectedGroupId(newGroup.id);
    setSelectedStores(new Set());
  };

  return (
    <div className="min-h-screen bg-slate-100 p-6">
      <div className="mx-auto max-w-7xl space-y-5">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm text-slate-500">
              Campaigns <ArrowRight className="h-3 w-3" /> Aisling Standard POS
            </div>
            <h1 className="flex items-center gap-2 text-3xl font-bold text-slate-950">
              <LayoutGrid className="h-7 w-7 text-primary" />
              Restructure Campaign
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              A task-first editor for seeing groups, splitting stores or formats, and previewing the impact before a new snapshot is saved.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline">Cancel</Button>
            <Button>Preview & Save</Button>
          </div>
        </header>

        <div className="grid gap-5 xl:grid-cols-[280px_minmax(0,1fr)_320px]">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Groups</CardTitle>
              <CardDescription>Visible structure first, actions second.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {groups.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => setSelectedGroupId(group.id)}
                  className={cn(
                    'w-full rounded-lg border bg-white p-3 text-left transition hover:border-primary/60 hover:shadow-sm',
                    selectedGroupId === group.id && 'border-primary ring-2 ring-primary/15',
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-slate-950">{group.name}</div>
                      <div className="mt-1 text-xs text-slate-500">{group.task}</div>
                    </div>
                    <Badge variant="secondary">{group.formats.length} formats</Badge>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                    <span className="rounded bg-slate-50 px-2 py-1">{group.stores.length} stores</span>
                    <span className="rounded bg-slate-50 px-2 py-1">
                      {group.formats.reduce((sum, format) => sum + format.quantity, 0)} qty
                    </span>
                  </div>
                </button>
              ))}

              <Button variant="outline" className="w-full justify-start">
                <Plus className="h-4 w-4" />
                Add empty group
              </Button>
            </CardContent>
          </Card>

          <main className="space-y-5">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle>{selectedGroup?.name}</CardTitle>
                    <CardDescription>
                      {selectedGroup?.stores.length} stores, {selectedGroup?.formats.length} formats, {selectedGroup?.task}
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm">
                      <Copy className="h-4 w-4" />
                      Duplicate
                    </Button>
                    <Button variant="outline" size="sm">
                      Rename
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 md:grid-cols-3">
                  <ModeButton
                    active={splitMode === 'formats'}
                    icon={<Boxes className="h-4 w-4" />}
                    title="Split By Formats"
                    description="Move chosen formats into a new group."
                    onClick={() => setSplitMode('formats')}
                  />
                  <ModeButton
                    active={splitMode === 'stores'}
                    icon={<Store className="h-4 w-4" />}
                    title="Split By Stores"
                    description="Move chosen stores into a new group."
                    onClick={() => setSplitMode('stores')}
                  />
                  <ModeButton
                    active={splitMode === 'matrix'}
                    icon={<LayoutGrid className="h-4 w-4" />}
                    title="Matrix View"
                    description="See store x format coverage at a glance."
                    onClick={() => setSplitMode('matrix')}
                  />
                </div>

                <div className="rounded-xl border bg-white p-4">
                  <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <h2 className="font-semibold text-slate-950">
                        {splitMode === 'formats' && 'Choose formats to move'}
                        {splitMode === 'stores' && 'Choose stores to move'}
                        {splitMode === 'matrix' && 'Coverage matrix'}
                      </h2>
                      <p className="text-sm text-slate-500">
                        {splitMode === 'matrix'
                          ? 'This makes the current group understandable before editing.'
                          : 'The left side stays in the source group. The selected items become a new group.'}
                      </p>
                    </div>
                    {splitMode !== 'matrix' && (
                      <div className="min-w-64">
                        <Label htmlFor="new-group-name">New group name</Label>
                        <Input
                          id="new-group-name"
                          value={newGroupName}
                          onChange={(event) => setNewGroupName(event.target.value)}
                        />
                      </div>
                    )}
                  </div>

                  {splitMode === 'formats' && (
                    <div className="space-y-2">
                      {selectedGroup?.formats.map((format) => (
                        <label
                          key={format.id}
                          className="flex cursor-pointer items-center justify-between rounded-lg border p-3 hover:bg-slate-50"
                        >
                          <span className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedFormats.has(format.id)}
                              onChange={() => toggleFormat(format.id)}
                              className="h-4 w-4"
                            />
                            <span>
                              <span className="block font-medium">{format.name}</span>
                              <span className="text-xs text-slate-500">Quantity {format.quantity}</span>
                            </span>
                          </span>
                          <Badge variant="outline">format</Badge>
                        </label>
                      ))}
                    </div>
                  )}

                  {splitMode === 'stores' && (
                    <div className="grid gap-2 md:grid-cols-2">
                      {selectedGroup?.stores.map((storeName) => (
                        <label
                          key={storeName}
                          className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 hover:bg-slate-50"
                        >
                          <input
                            type="checkbox"
                            checked={selectedStores.has(storeName)}
                            onChange={() => toggleStore(storeName)}
                            className="h-4 w-4"
                          />
                          <span className="font-medium">{storeName}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {splitMode === 'matrix' && (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[640px] border-collapse text-sm">
                        <thead>
                          <tr>
                            <th className="border-b p-3 text-left text-slate-500">Store</th>
                            {selectedGroup?.formats.map((format) => (
                              <th key={format.id} className="border-b p-3 text-left text-slate-500">
                                {format.name}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {selectedGroup?.stores.map((storeName) => (
                            <tr key={storeName}>
                              <td className="border-b p-3 font-medium">{storeName}</td>
                              {selectedGroup.formats.map((format) => (
                                <td key={format.id} className="border-b p-3">
                                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {splitMode !== 'matrix' && (
                    <div className="mt-4 flex justify-end">
                      <Button onClick={splitMode === 'formats' ? splitSelectedFormats : splitSelectedStores}>
                        <Scissors className="h-4 w-4" />
                        Split into New Group
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </main>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Impact Preview</CardTitle>
              <CardDescription>Always visible before save.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Metric label="Groups" value={campaignSummary.groups} />
                <Metric label="Stores" value={campaignSummary.stores} />
                <Metric label="Formats" value={campaignSummary.formats} />
                <Metric label="Quantity" value={campaignSummary.quantity} />
              </div>

              <div className="rounded-lg border bg-emerald-50 p-3 text-sm text-emerald-800">
                <div className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 className="h-4 w-4" />
                  Valid structure
                </div>
                <p className="mt-1 text-emerald-700">No empty groups, no duplicate formats within a group, and stores remain covered.</p>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-semibold">Save Summary</h3>
                {groups.map((group) => (
                  <div key={group.id} className="rounded-lg border bg-white p-3 text-sm">
                    <div className="font-medium">{group.name}</div>
                    <div className="mt-1 text-slate-500">
                      {group.stores.length} stores x {group.formats.length} formats
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ModeButton({
  active,
  icon,
  title,
  description,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-lg border bg-white p-3 text-left transition hover:border-primary/60',
        active && 'border-primary bg-primary/5 ring-2 ring-primary/10',
      )}
    >
      <div className="flex items-center gap-2 font-semibold text-slate-950">
        {icon}
        {title}
      </div>
      <p className="mt-1 text-xs text-slate-500">{description}</p>
    </button>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border bg-white p-3">
      <div className="text-2xl font-bold text-slate-950">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}
